import { type UIMessage, useChat } from "@ai-sdk/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import { useJourneyChatContext } from "../../hooks/useJourneyChatContext";
import type { User } from "../../hooks/useUser";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { uploadFilesToBlob } from "../../services/fileUploadService";
import styles from "./ChatContainer.module.css";
import { ChatInput } from "./ChatInput";
import { MessageList } from "./MessageList";
import { PersonalizedGreeting } from "./PersonalizedGreeting";

interface ChatContainerProps {
	id: string;
	user: User;
	initialMessages?: Array<{
		id: string;
		role: string;
		content: string | Array<{ text?: string }>;
		createdAt?: Date | string;
	}>;
	onFocus?: () => void;
	onBlur?: () => void;
	isExpanded?: boolean;
	onMessagesChange?: (messageCount: number) => void;
	chatState?: "compact" | "expandedInput" | "active";
	actionRow?: React.ReactNode;
	onScrollToBottomRef?: (scrollFn: () => void) => void;
	onSuggestClick?: () => void;
}

type MessagePart =
	| { type: "text"; text: string }
	| { type: "file"; url: string; mediaType: string };

function buildMessageParts(
	text: string,
	files?: Array<{ url: string; mediaType: string }>,
	metadata?: Record<string, unknown>,
): MessagePart[] {
	const parts: MessagePart[] = [
		{ type: "text", text, ...metadata } as MessagePart,
	];
	if (files) {
		for (const file of files) {
			parts.push({ type: "file", url: file.url, mediaType: file.mediaType });
		}
	}
	return parts;
}

export function ChatContainer({
	id,
	user,
	initialMessages = [],
	onFocus,
	onBlur,
	isExpanded,
	onMessagesChange,
	chatState,
	actionRow,
	onScrollToBottomRef,
	onSuggestClick,
}: ChatContainerProps) {
	const [input, setInput] = useState("");
	const [fileDataUrls, setFileDataUrls] = useState<
		Array<{ file: File; dataUrl: string }>
	>([]);
	const scrollRef = useRef<HTMLDivElement>(null);
	// Sentinel at the end of the message list — scrolled into view to pin the
	// latest message/thinking indicator, robust to which ancestor actually
	// scrolls (86ey1fqaa).
	const bottomAnchorRef = useRef<HTMLDivElement>(null);
	// Last text the user tried to send — restored into the input if the send
	// fails, so a "failed to send" error is recoverable (86ey1fqaa).
	const lastSentRef = useRef("");
	const { getAccessTokenSilently } = useAuth0();
	const { logChat } = useAnalytics();
	const { pendingMessage, consumeMessage, prefillQuestion, consumePrefill } =
		useChatActions();
	const { t } = useClientTranslations("chat");

	// Format initial messages to match AI SDK UIMessage type
	const formattedInitialMessages: UIMessage[] = initialMessages.map((msg) => {
		// If content is already an array of parts, preserve it (with metadata)
		if (Array.isArray(msg.content)) {
			return {
				id: msg.id,
				role: msg.role as "user" | "assistant",
				parts: msg.content.map((part) => ({
					type: "text" as const,
					text: part.text || "",
					// Preserve custom metadata (e.g., isAmaQuestion)
					...(part as Record<string, unknown>),
				})),
			};
		}

		// Otherwise, create a single text part
		return {
			id: msg.id,
			role: msg.role as "user" | "assistant",
			parts: [
				{
					type: "text" as const,
					text:
						typeof msg.content === "string"
							? msg.content
							: JSON.stringify(msg.content),
				},
			],
		};
	});

	// Current journey stage → sent in the chat body so Eve grounds answers in the
	// user's actual step (handoff onboarding-v5-eve-journey-step-context). Null
	// when there's no active journey/step → omitted from the body.
	// Held in a ref: the AI SDK captures the transport closure on first mount,
	// before the async journey load resolves, so reading the live value at send
	// time is required (a direct closure would always see the initial null).
	const journeyContext = useJourneyChatContext(user);
	const journeyContextRef = useRef(journeyContext);
	journeyContextRef.current = journeyContext;

	const queryClient = useQueryClient();
	const { messages, sendMessage, status, error, regenerate } = useChat({
		id,
		messages: formattedInitialMessages,
		transport: new DefaultChatTransport({
			api: `${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/chat`,
			credentials: "include",
			headers: async () => {
				const token = await getAccessTokenSilently({
					authorizationParams: {
						audience: import.meta.env.VITE_AUTH0_AUDIENCE,
					},
				});

				return {
					Authorization: `Bearer ${token}`,
				};
			},
			prepareSendMessagesRequest: ({ messages }) => {
				const jc = journeyContextRef.current;
				return {
					body: {
						id,
						messages,
						...(jc ? { journeyContext: jc } : {}),
					},
				};
			},
		}),
		onFinish: ({ message }) => {
			// Intentionally NOT scrolling to the bottom here — for a long answer that
			// hides the start of Eve's response. The start is pinned to the top when
			// the assistant message first appears (see the auto-scroll effect below).
			// A brand-new chat is persisted server-side on the first message, so the
			// cached chats list / today-chat are now stale — invalidate them so
			// returning to the communication page shows the correct latest chat
			// (previously only self-healed after an app restart).
			queryClient.invalidateQueries({ queryKey: ["chats"] });
			queryClient.invalidateQueries({ queryKey: ["chat", "today"] });
			// This chat's cached history (["chat", id]) doesn't include the turn we
			// just exchanged; invalidate it so returning to this chat re-reads the
			// full history instead of a stale snapshot missing the newest message.
			queryClient.invalidateQueries({ queryKey: ["chat", id] });
			// Track AI response received
			const responseLength =
				message.parts
					?.filter(
						(p): p is { type: "text"; text: string } => p.type === "text",
					)
					.map((p) => p.text || "")
					.join("").length || 0;
			logChat("message_received", {
				message_length: responseLength,
				chat_id: id,
			});
		},
		onError: (err) => {
			console.error("Chat error:", err);
			toast.error(t("error.messageFailed"));
			// Restore the failed text so the user can resend instead of losing it
			// (don't clobber a new draft they've already started typing).
			setInput((cur) => cur || lastSentRef.current);
			logChat("error", {
				chat_id: id,
				error_message: err.message,
			});
		},
	});

	// isStreaming: AI is actively sending response chunks
	const isStreaming = status === "streaming";
	// isThinking: request sent, waiting for AI to start responding
	const isThinking = status === "submitted";
	// Combined loading state for UI elements that should be disabled during either
	const isLoading = isStreaming || isThinking;

	// Notify parent when message count changes
	useEffect(() => {
		onMessagesChange?.(messages.length);
	}, [messages.length, onMessagesChange]);

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setInput(e.target.value);
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!input.trim() || isLoading) return;

		const userMessage = input.trim();
		lastSentRef.current = userMessage;
		setInput("");

		logChat("message_sent", {
			message_length: userMessage.length,
			has_files: fileDataUrls.length > 0,
			file_count: fileDataUrls.length,
		});

		// Upload files to Vercel Blob and get public URLs
		const uploadedFiles =
			fileDataUrls.length > 0
				? await uploadFilesToBlob(fileDataUrls)
				: undefined;

		// Send message with parts
		await sendMessage({
			role: "user",
			parts: buildMessageParts(userMessage, uploadedFiles),
		});

		setFileDataUrls([]);
	};

	const scrollToBottom = useCallback(() => {
		// rAF so the just-added message / "thinking" indicator is laid out before
		// we scroll. scrollIntoView on the bottom sentinel scrolls whichever
		// ancestor is actually scrollable (the messages container OR the page),
		// which a direct scrollRef.scrollTop missed in some layouts.
		requestAnimationFrame(() => {
			if (bottomAnchorRef.current) {
				bottomAnchorRef.current.scrollIntoView({ block: "end" });
			} else if (scrollRef.current) {
				scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
			}
		});
	}, []);

	// Expose scrollToBottom to parent via callback ref
	useEffect(() => {
		onScrollToBottomRef?.(scrollToBottom);
	}, [scrollToBottom, onScrollToBottomRef]);

	// Pin the TOP of Eve's response to the top of the scroll area so the user
	// reads it from the beginning, instead of chasing the bottom of a long answer.
	const scrolledAssistantIdRef = useRef<string | null>(null);
	const scrollAssistantStartIntoView = useCallback((messageId: string) => {
		requestAnimationFrame(() => {
			const el = scrollRef.current?.querySelector(
				`[data-message-id="${messageId}"]`,
			);
			(el as HTMLElement | null)?.scrollIntoView({ block: "start" });
		});
	}, []);

	// Auto-scroll. While the user's message is last (just sent / "Eve is thinking"),
	// keep the bottom in view so their message + the thinking indicator show
	// (86ey1fqaa). Once Eve's assistant message appears, scroll its START into view
	// once (guarded by id) and leave it there as it streams below.
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-evaluate on message/thinking changes; body reads messages.
	useEffect(() => {
		const last = messages[messages.length - 1];
		if (!last) return;
		if (last.role === "assistant") {
			if (scrolledAssistantIdRef.current !== last.id) {
				scrolledAssistantIdRef.current = last.id;
				scrollAssistantStartIntoView(last.id);
			}
		} else {
			scrollToBottom();
		}
	}, [messages, isThinking, scrollToBottom, scrollAssistantStartIntoView]);

	// Handle pending messages from external triggers (e.g., "Explore with Eve" button)
	useEffect(() => {
		if (pendingMessage && !isLoading) {
			const message = consumeMessage();
			if (message) {
				sendMessage({
					role: "user",
					parts: buildMessageParts(
						message.text,
						message.files,
						message.metadata,
					),
				});
			}
		}
	}, [pendingMessage, isLoading, consumeMessage, sendMessage]);

	// Handle prefill questions from external triggers (e.g., "Ask Eve" button on ContentCard)
	// Unlike pendingMessage, this only populates the input — user must tap send
	// Only apply when input is empty to avoid overwriting user's draft
	useEffect(() => {
		if (prefillQuestion && !isLoading && !input) {
			const text = consumePrefill();
			if (text) {
				setInput(text);
			}
		}
	}, [prefillQuestion, isLoading, input, consumePrefill]);

	const containerStateClass = chatState
		? chatState === "active"
			? styles.activeState
			: chatState === "expandedInput"
				? styles.expandedInputState
				: styles.emptyState
		: messages.length > 0
			? styles.activeState
			: isExpanded
				? styles.expanded
				: styles.emptyState;

	return (
		<div className={`${styles.container} ${containerStateClass}`}>
			<div
				ref={scrollRef}
				className={`${styles.messagesContainer} ${!chatState && isExpanded === false ? styles.collapsed : ""}`}
				data-scrollable="true"
			>
				{messages.length === 0 && !isLoading && (
					<div className={styles.greetingWrapper}>
						<PersonalizedGreeting user={user} />
					</div>
				)}
				<MessageList
					messages={messages}
					isLoading={isLoading}
					isThinking={isThinking}
					userImage={user.userImage ?? null}
				/>
				{/* Scroll target — kept at the very bottom of the message list. */}
				<div ref={bottomAnchorRef} aria-hidden="true" />
			</div>

			{/* Stream/generation error → inline message + retry (the API now sends a
			    friendly error part instead of a dead stream). */}
			{error && !isLoading && (
				<div className={styles.errorBanner} role="alert">
					<span className={styles.errorBannerText}>
						{t("error.messageFailed")}
					</span>
					<button
						type="button"
						className={styles.errorBannerRetry}
						onClick={() => regenerate()}
					>
						{t("buttons.retry")}
					</button>
				</div>
			)}

			{actionRow}

			<div className={styles.inputContainer}>
				<ChatInput
					input={input}
					setInput={setInput}
					handleInputChange={handleInputChange}
					handleSubmit={handleSubmit}
					isLoading={isLoading}
					fileDataUrls={fileDataUrls}
					setFileDataUrls={setFileDataUrls}
					onSuggestClick={onSuggestClick}
					onFocus={onFocus}
					onBlur={onBlur}
					isExpanded={isExpanded}
				/>
			</div>
		</div>
	);
}
